import { useState } from "react";
import axiosInstance from "../../../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../../../constants/config";
import { getFullName } from "../../../../utils/urlUtils";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const DEFAULT_EXPORT_COLUMNS = {
  name: true, admissionNumber: true, username: true, rollNumber: true,
  class: true, section: true, status: true, firstName: false,
  middleName: false, lastName: false, fatherName: true, motherName: true,
  aadhaarCard: true, email: true, phoneNumber: true, birthday: true,
  bloodGroup: true, gender: true, transportationType: true, busRoute: true,
  fatherContactNumber: true, motherContactNumber: true, guardianName: true,
  guardianContactNumber: true, address: true,
  addressStreet: false, addressCity: false, addressState: false,
  addressPincode: false, addressCountry: false, profileImage: false,
  createdAt: false, updatedAt: false,
};

const ALL_COLUMN_LABELS = {
  name: "Name", admissionNumber: "Admission Number", username: "Username",
  rollNumber: "Roll Number", class: "Class", section: "Section", status: "Status",
  firstName: "First Name", middleName: "Middle Name", lastName: "Last Name",
  fatherName: "Father Name", motherName: "Mother Name", aadhaarCard: "Aadhaar Card",
  email: "Email", phoneNumber: "Phone Number", address: "Address (Full)",
  addressStreet: "Address Street", addressCity: "Address City",
  addressState: "Address State", addressPincode: "Address Pincode",
  addressCountry: "Address Country", bloodGroup: "Blood Group", birthday: "Birthday",
  gender: "Gender", guardianName: "Guardian Name",
  guardianContactNumber: "Guardian Contact Number",
  fatherContactNumber: "Father Contact Number",
  motherContactNumber: "Mother Contact Number",
  transportationType: "Transportation Type", busRoute: "Bus Route",
  profileImage: "Profile Image URL", createdAt: "Created At", updatedAt: "Updated At",
};

function getStudentFieldValue(student, key, routeMap = {}) {
  switch (key) {
    case "name": return getFullName(student);
    case "class": return student.classMapping?.class?.name || "Unassigned";
    case "section": return student.classMapping?.section?.name || "Unassigned";
    case "rollNumber": return student.classMapping?.rollNumber || "Not Set";
    case "status": return student.status || "Active";
    case "address":
      return student.address
        ? `${student.address.street || ""}, ${student.address.city || ""}, ${student.address.state || ""}, ${student.address.pincode || ""}, ${student.address.country || ""}`
            .replace(/(^,\s*)|(,\s*$)/g, "").replace(/,\s*,/g, ",")
        : "Not provided";
    case "addressStreet": return student.address?.street || "";
    case "addressCity": return student.address?.city || "";
    case "addressState": return student.address?.state || "";
    case "addressPincode": return student.address?.pincode || "";
    case "addressCountry": return student.address?.country || "";
    case "birthday":
      return student.birthday ? new Date(student.birthday).toLocaleDateString() : "Not provided";
    case "gender": return student.gender || "";
    case "transportationType":
      return student.transportationType
        ? student.transportationType.charAt(0).toUpperCase() + student.transportationType.slice(1)
        : "";
    case "busRoute":
      return student.transportationType === "bus" ? (routeMap[student._id] || "Not assigned") : "N/A";
    case "bloodGroup": return student.bloodGroup || "";
    case "fatherContactNumber": return student.fatherContactNumber || "";
    case "motherContactNumber": return student.motherContactNumber || "";
    case "guardianName": return student.guardianName || "";
    case "guardianContactNumber": return student.guardianContactNumber || "";
    case "username": return student.username || "";
    case "firstName": return student.firstName || "";
    case "middleName": return student.middleName || "";
    case "lastName": return student.lastName || "";
    case "profileImage": return student.profileImage || "";
    case "createdAt":
      return student.createdAt ? new Date(student.createdAt).toLocaleDateString() : "";
    case "updatedAt":
      return student.updatedAt ? new Date(student.updatedAt).toLocaleDateString() : "";
    default: return student[key] || "";
  }
}

async function fetchBusRoutes(students) {
  const routePromises = students
    .filter((s) => s.transportationType === "bus")
    .map(async (student) => {
      try {
        const token = localStorage.getItem("token");
        const res = await axiosInstance.get(API_ENDPOINTS.vehicleRouteStudent(student._id), {
          headers: { Authorization: `Bearer ${token}` },
        });
        const routeName =
          res.data.data.vehicleRoute?.name ||
          res.data.data.vehicleRoute?.vehicle?.vehicleNumber ||
          "Not assigned";
        return { studentId: student._id, routeName };
      } catch {
        return { studentId: student._id, routeName: "Not assigned" };
      }
    });

  const routeResults = await Promise.all(routePromises);
  const routeMap = {};
  routeResults.forEach(({ studentId, routeName }) => {
    routeMap[studentId] = routeName;
  });
  return routeMap;
}

export function useStudentExport({ fetchAllStudentsForExport }) {
  const [exportColumns, setExportColumns] = useState(DEFAULT_EXPORT_COLUMNS);
  const [previewData, setPreviewData] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isColumnSelectionOpen, setIsColumnSelectionOpen] = useState(false);

  const getSelectedKeys = () => Object.keys(exportColumns).filter((key) => exportColumns[key]);

  const getHeaders = () =>
    getSelectedKeys().map(
      (key) =>
        ALL_COLUMN_LABELS[key] ||
        key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())
    );

  const handleExportCSV = async () => {
    const students = await fetchAllStudentsForExport();
    const routeMap = await fetchBusRoutes(students);
    const selectedKeys = getSelectedKeys();
    const headers = getHeaders();

    let csvContent = headers.join(",") + "\n";
    students.forEach((student) => {
      const row = selectedKeys
        .map((key) => getStudentFieldValue(student, key, routeMap))
        .map((value) => `"${value.toString().replace(/"/g, '""')}"`);
      csvContent += row.join(",") + "\n";
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `students_${new Date().toISOString().split("T")[0]}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportExcel = async (format = "xlsx") => {
    const students = await fetchAllStudentsForExport();
    if (students.length === 0) return;

    const routeMap = await fetchBusRoutes(students);
    const selectedKeys = getSelectedKeys();
    const headers = getHeaders();

    let csvContent = headers.join(",") + "\n";
    students.forEach((student) => {
      const row = selectedKeys
        .map((key) => getStudentFieldValue(student, key, routeMap))
        .map((value) => `"${value.toString().replace(/"/g, '""')}"`);
      csvContent += row.join(",") + "\n";
    });

    const ext = format === "xls" ? "xls" : "xlsx";
    const blob = new Blob([csvContent], { type: "application/vnd.ms-excel" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `students_${new Date().toISOString().split("T")[0]}.${ext}`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportPDF = async () => {
    const students = await fetchAllStudentsForExport();
    if (students.length === 0) return;

    try {
      const routeMap = await fetchBusRoutes(students);
      const selectedKeys = getSelectedKeys();
      const headers = getHeaders();

      const doc = new jsPDF();
      doc.setFontSize(18);
      doc.text("Students Report", 14, 22);
      doc.setFontSize(11);
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 30);

      const data = students.map((student) =>
        selectedKeys.map((key) => getStudentFieldValue(student, key, routeMap))
      );

      autoTable(doc, {
        head: [headers],
        body: data,
        startY: 40,
        theme: "grid",
        styles: { fontSize: 8, cellPadding: 3 },
        headStyles: { fillColor: [66, 139, 202], textColor: 255, fontStyle: "bold" },
      });

      doc.save(`students_${new Date().toISOString().split("T")[0]}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to generate PDF. Please try again.");
    }
  };

  const handlePreviewExport = async () => {
    const students = await fetchAllStudentsForExport();
    if (students.length === 0) return;

    const previewStudents = students.slice(0, 5);
    const routeMap = await fetchBusRoutes(previewStudents);
    const selectedKeys = getSelectedKeys();

    const previewRows = previewStudents.map((student) => {
      const row = {};
      selectedKeys.forEach((key) => {
        row[key] = getStudentFieldValue(student, key, routeMap);
      });
      return row;
    });

    setPreviewData(previewRows);
    setIsPreviewOpen(true);
  };

  const resetExportColumns = () => setExportColumns(DEFAULT_EXPORT_COLUMNS);

  const selectAllColumns = () => {
    const all = {};
    Object.keys(exportColumns).forEach((key) => { all[key] = true; });
    setExportColumns(all);
  };

  const deselectAllColumns = () => {
    const none = {};
    Object.keys(exportColumns).forEach((key) => { none[key] = false; });
    setExportColumns(none);
  };

  return {
    exportColumns, setExportColumns,
    previewData, isPreviewOpen, setIsPreviewOpen,
    isColumnSelectionOpen, setIsColumnSelectionOpen,
    handleExportCSV, handleExportExcel, handleExportPDF, handlePreviewExport,
    resetExportColumns, selectAllColumns, deselectAllColumns,
  };
}
